import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  EnvelopeSimple,
  Lightning,
  MagnifyingGlass,
  PaperPlaneTilt,
  PencilSimple,
  Play,
  ShieldCheck,
  Sparkle,
  X,
} from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';

/* ─────────────────────────────────────────────── */
/* PROCESSING STEPS                                 */
/* ─────────────────────────────────────────────── */
const STEPS = [
  { label: 'Receiving enquiry',   icon: EnvelopeSimple, duration: 800  },
  { label: 'Extracting details',  icon: MagnifyingGlass, duration: 900  },
  { label: 'Preparing response',  icon: Sparkle,        duration: 1200 },
];

/* ─────────────────────────────────────────────── */
/* MAIN COMPONENT                                   */
/* ─────────────────────────────────────────────── */
export default function TestEnquiryModal({ workerId, workerName, orgId, isOpen, onClose, onComplete }) {
  const [phase, setPhase] = useState('idle');       // idle | processing | result | editing | confirmed
  const [activeStep, setActiveStep] = useState(-1);
  const [data, setData] = useState(null);           // { sample_enquiry, reply }
  const [editBody, setEditBody] = useState('');
  const [error, setError] = useState('');
  const processRef = useRef(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhase('idle');
      setActiveStep(-1);
      setData(null);
      setEditBody('');
      setError('');
      processRef.current = false;
    }
  }, [isOpen]);

  const startTest = async () => {
    if (processRef.current) return;
    processRef.current = true;
    setPhase('processing');
    setActiveStep(0);
    setError('');

    // Animate processing steps while backend call runs
    const stepTimers = [];
    let stepIdx = 0;
    const advanceStep = () => {
      stepIdx++;
      if (stepIdx < STEPS.length) {
        setActiveStep(stepIdx);
        stepTimers.push(setTimeout(advanceStep, STEPS[stepIdx].duration));
      }
    };
    stepTimers.push(setTimeout(advanceStep, STEPS[0].duration));

    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations/${workerId}/test-enquiry`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
        orgId
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Test enquiry failed');
      }
      const result = await res.json();

      // Ensure minimum visible processing time
      await new Promise(r => setTimeout(r, 600));
      stepTimers.forEach(clearTimeout);
      setActiveStep(STEPS.length); // all done
      await new Promise(r => setTimeout(r, 400));

      setData(result);
      setEditBody(result.reply?.body || '');
      setPhase('result');
    } catch (err) {
      stepTimers.forEach(clearTimeout);
      setError(err.message || 'Something went wrong');
      setPhase('idle');
      processRef.current = false;
    }
  };

  const handleApprove = () => {
    setPhase('confirmed');
    onComplete?.();
  };

  const handleEdit = () => {
    setPhase('editing');
  };

  const handleSaveEdit = () => {
    setData(prev => ({
      ...prev,
      reply: { ...prev.reply, body: editBody },
    }));
    setPhase('result');
  };

  const handleDismiss = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-[#111827] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="test-enquiry-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[rgba(255,255,255,0.08)]">
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="fill" className="text-[#00E5C0]" />
              <span className="text-sm font-semibold text-[#F0F4F8]">Test enquiry</span>
              <span className="px-2 py-0.5 rounded text-[9px] font-medium bg-[rgba(245,158,11,0.1)] text-[#F59E0B] border border-[rgba(245,158,11,0.15)]">
                Test mode
              </span>
            </div>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-[rgba(255,255,255,0.04)] text-[#7D8896] hover:text-white transition-colors" data-testid="test-enquiry-close">
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 max-h-[70vh] overflow-y-auto">
            <AnimatePresence mode="wait">

              {/* ════ IDLE — Start CTA ════ */}
              {phase === 'idle' && (
                <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="text-center py-6">
                  <div className="w-14 h-14 rounded-2xl bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.15)] flex items-center justify-center mx-auto mb-5">
                    <EnvelopeSimple size={28} className="text-[#00E5C0]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">See your worker in action</h3>
                  <p className="text-sm text-[#7D8896] mb-1 max-w-sm mx-auto">
                    We'll send a realistic customer enquiry and show you exactly how {workerName || 'your worker'} handles it.
                  </p>
                  <p className="text-[11px] text-[#5C6778] mb-6">No real messages will be sent.</p>

                  {error && (
                    <div className="mb-4 px-3 py-2 rounded-lg bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] text-xs text-red-400">
                      {error}
                    </div>
                  )}

                  <button onClick={startTest} data-testid="start-test-enquiry-btn"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-bold transition-colors shadow-lg shadow-[rgba(0,229,192,0.12)]">
                    <Play size={18} weight="fill" /> Run a test enquiry
                  </button>
                </motion.div>
              )}

              {/* ════ PROCESSING ════ */}
              {phase === 'processing' && (
                <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="py-4">
                  <div className="space-y-3">
                    {STEPS.map((step, idx) => {
                      const Icon = step.icon;
                      const isDone = activeStep > idx;
                      const isCurrent = activeStep === idx;
                      return (
                        <motion.div key={idx}
                          initial={{ opacity: 0, x: -8 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.15 }}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                            isDone ? 'bg-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.12)]'
                            : isCurrent ? 'bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.2)]'
                            : 'bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)]'
                          }`}
                          data-testid={`processing-step-${idx}`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                            isDone ? 'bg-[rgba(0,229,192,0.12)]' : isCurrent ? 'bg-[rgba(0,229,192,0.1)]' : 'bg-[rgba(255,255,255,0.04)]'
                          }`}>
                            {isDone ? (
                              <CheckCircle size={18} weight="fill" className="text-[#00E5C0]" />
                            ) : isCurrent ? (
                              <CircleNotch size={18} className="text-[#00E5C0] animate-spin" />
                            ) : (
                              <Icon size={18} className="text-[#5C6778]" />
                            )}
                          </div>
                          <span className={`text-sm font-medium ${
                            isDone ? 'text-[#00E5C0]' : isCurrent ? 'text-white' : 'text-[#7D8896]'
                          }`}>
                            {step.label}{isDone ? '' : isCurrent ? '...' : ''}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* ════ RESULT ════ */}
              {phase === 'result' && data && (
                <motion.div key="result" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="space-y-4">

                  {/* Incoming enquiry / scenario */}
                  <div data-testid="test-enquiry-incoming">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-[rgba(99,102,241,0.12)] flex items-center justify-center">
                        <EnvelopeSimple size={12} weight="fill" className="text-[#818CF8]" />
                      </div>
                      <span className="text-xs font-semibold text-[#818CF8]">Scenario</span>
                      <span className="text-[10px] text-[#5C6778]">{data.sample_enquiry.customer_name}</span>
                    </div>
                    <div className="bg-[rgba(99,102,241,0.04)] border border-[rgba(99,102,241,0.1)] rounded-xl px-4 py-3">
                      <p className="text-[11px] text-[#7D8896] mb-1 font-medium">Subject: {data.sample_enquiry.subject}</p>
                      <p className="text-sm text-[#C8D1DC] leading-relaxed whitespace-pre-wrap">{data.sample_enquiry.message}</p>
                    </div>
                  </div>

                  {/* Generated reply — only show if there's an actual response */}
                  {data.reply?.body ? (
                  <div data-testid="test-enquiry-reply">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full bg-[rgba(0,229,192,0.12)] flex items-center justify-center">
                        <PaperPlaneTilt size={12} weight="fill" className="text-[#00E5C0]" />
                      </div>
                      <span className="text-xs font-semibold text-[#00E5C0]">Worker response</span>
                    </div>

                    {/* Summary line */}
                    {data.summary && (
                      <div className="mb-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)]">
                        <p className="text-[11px] text-[#B0B8C4] leading-relaxed">{data.summary}</p>
                      </div>
                    )}

                    <div className="bg-[rgba(0,229,192,0.03)] border border-[rgba(0,229,192,0.12)] rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3 mb-2 pb-2 border-b border-[rgba(255,255,255,0.04)]">
                        <span className="text-[10px] text-[#7D8896]">To:</span>
                        <span className="text-xs text-[#C8D1DC]">{data.sample_enquiry.customer_name}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-3 pb-2 border-b border-[rgba(255,255,255,0.04)]">
                        <span className="text-[10px] text-[#7D8896]">Subject:</span>
                        <span className="text-xs text-[#C8D1DC] font-medium">{data.reply.subject}</span>
                      </div>
                      <p className="text-sm text-[#F0F4F8] leading-relaxed whitespace-pre-wrap">{data.reply.body}</p>
                    </div>
                  </div>
                  ) : null}

                  {/* Decision buttons — only show if there's a response to approve */}
                  {data.reply?.body ? (
                  <div className="flex items-center gap-2 pt-2" data-testid="test-enquiry-actions">
                    <button onClick={handleApprove} data-testid="test-approve-btn"
                      className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-bold transition-colors">
                      <CheckCircle size={16} weight="bold" /> Approve & send
                    </button>
                    <button onClick={handleEdit} data-testid="test-edit-btn"
                      className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-sm font-medium text-[#B0B8C4] hover:text-white hover:border-[rgba(255,255,255,0.15)] transition-colors">
                      <PencilSimple size={14} /> Edit
                    </button>
                    <button onClick={handleDismiss} data-testid="test-dismiss-btn"
                      className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm text-[#7D8896] hover:text-[#B0B8C4] transition-colors">
                      Dismiss
                    </button>
                  </div>
                  ) : (
                  <div className="flex items-center gap-2 pt-2">
                    <button onClick={handleDismiss} data-testid="test-dismiss-btn"
                      className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-sm font-medium text-[#B0B8C4] hover:text-white hover:border-[rgba(255,255,255,0.15)] transition-colors">
                      Close
                    </button>
                  </div>
                  )}
                </motion.div>
              )}

              {/* ════ EDITING ════ */}
              {phase === 'editing' && data && (
                <motion.div key="editing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-[#7D8896] mb-2 block">Edit reply</label>
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={8}
                      autoFocus
                      data-testid="test-edit-textarea"
                      className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(0,229,192,0.2)] rounded-xl text-sm text-[#F0F4F8] focus:outline-none focus:border-[#00E5C0] transition-colors resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={handleSaveEdit} data-testid="test-save-edit-btn"
                      className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-bold transition-colors">
                      <CheckCircle size={16} weight="bold" /> Save changes
                    </button>
                    <button onClick={() => setPhase('result')}
                      className="px-4 py-3 rounded-xl text-sm text-[#7D8896] hover:text-white transition-colors">
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ════ CONFIRMED ════ */}
              {phase === 'confirmed' && (
                <motion.div key="confirmed" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  className="text-center py-6">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                    className="w-14 h-14 rounded-2xl bg-[rgba(0,229,192,0.12)] border border-[rgba(0,229,192,0.2)] flex items-center justify-center mx-auto mb-5">
                    <CheckCircle size={32} weight="fill" className="text-[#00E5C0]" />
                  </motion.div>

                  <h3 className="text-lg font-bold text-white mb-1">Reply sent successfully</h3>
                  <p className="text-sm text-[#B0B8C4] mb-1">Lead saved to your inbox</p>

                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[rgba(245,158,11,0.08)] text-[10px] font-medium text-[#F59E0B] border border-[rgba(245,158,11,0.12)] mb-6">
                    <ShieldCheck size={12} /> Test mode. No real messages sent.
                  </div>

                  {/* Post-WOW CTA */}
                  <div className="bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 mt-2">
                    <p className="text-sm font-semibold text-white mb-1">Ready to handle real enquiries?</p>
                    <p className="text-xs text-[#7D8896] mb-4">Connect your email to start processing real customer messages automatically.</p>
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('open-tools-modal')); }}
                        data-testid="connect-email-cta"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-sm font-semibold transition-colors">
                        <EnvelopeSimple size={14} weight="bold" /> Connect Gmail / Outlook
                        <ArrowRight size={12} />
                      </button>
                    </div>
                  </div>

                  <button onClick={onClose} className="mt-4 text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors" data-testid="test-done-btn">
                    Done
                  </button>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </>
  );
}
